import React from "react";
import { Formik, FormikProps, Field, FieldProps } from "formik";
import { Grid, TextField } from "@material-ui/core";
import * as Yup from "yup";
import { Values } from "Tools/Values";
import { STFormWrapper } from "@src/Components/Ui/STFormWrapper";
import renderCommonFormField from "@src/Components/Common/FormFields/renderCommonFormField";
import FormSwitch from "@src/Components/Common/FormFields/FormSwitch/FormSwitch";
import { inject, observer } from "mobx-react";
import { IValuesStore } from "@src/Stores/Values/types";
import { IManagementStore } from "@src/Stores/Management/types";
import FileField from "./FileField/FileField";
import ColorField from "./ColorField/ColorField";
import { ButtonsForm } from "@src/Components/Common/ButtonsForm/ButtonsForm";
import { Theme } from "@src/Interfaces/Entity/Theme";
import { computed } from "mobx";
import { STFormSimpleSelect, LocationFormSimpleSelect, STGrid } from "./styles";
import { IAuthStore } from "@src/Stores/Auth/types";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";

const FIVE_MB = 5e6;

const getValidationSchema = (isLoginThemeTypeSelected: boolean) => {
  if (isLoginThemeTypeSelected) {
    return Yup.object().shape({
      name: Yup.string().required(() => {
        return Values.getLocalizationValue("fieldRequired");
      }),
      loginThemeDetails: Yup.object().shape({
        loginBackgroundPortraitImage: Yup.mixed().test(
          "maxSize",
          () => Values.getLocalizationValue("maxSize"),
          (value) => value.size < FIVE_MB
        ),
        loginBackgroundLandscapeImage: Yup.mixed().test(
          "maxSize",
          () => Values.getLocalizationValue("maxSize"),
          (value) => value.size < FIVE_MB
        ),
        loginLogoImage: Yup.mixed().test(
          "maxSize",
          () => Values.getLocalizationValue("maxSize"),
          (value) => value.size < FIVE_MB
        ),
      }),
    });
  }
  return Yup.object().shape({
    name: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    secondaryColor: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    applicationThemeDetails: Yup.object().shape({
      primaryColor: Yup.string().required(() => {
        return Values.getLocalizationValue("fieldRequired");
      }),
      applicationLogoImage: Yup.mixed().test(
        "maxSize",
        () => Values.getLocalizationValue("maxSize"),
        (value) => value.size < FIVE_MB
      ),
    }),
  });
};

interface IProps {
  title: string;
  initialValues: Theme;
  onSubmit: (values: Theme) => void;
  onClose: () => void;
  values?: IValuesStore;
  management?: IManagementStore;
  auth?: IAuthStore;
  isEdit: boolean;
  loginBackgroundLandscapeImageUrl?: string;
  loginBackgroundPortraitImageUrl?: string;
  applicationLogoImageUrl?: string;
  loginLogoImageUrl?: string;
}

interface IFormValues {
  type: number;
}

@inject("auth", "values", "management")
@observer
class ThemeForm extends React.Component<IProps> {
  @computed get themeTypes() {
    let themeTypes = Object.entries(this.props.values.themeTypes).map(([typeThemeString, typeThemeValue]) => ({
      name: typeThemeString,
      value: typeThemeValue,
    }));
      if (!this.props.auth.user.location.primary && !this.props.isEdit) {
      themeTypes = themeTypes.filter((type) => type.value !== this.props.values.themeTypes.Application);
    }
    return themeTypes;
  }

  @computed get isNew(): boolean {
    return this.props.initialValues.name === "";
  }

  getIsAppTheme = (formikProps: FormikProps<Theme>) => {
    return formikProps.values.type == this.props.values.themeTypes.Application;
  };

  handleTypeChange = (formikProps: FormikProps<Theme>) => (item: any) => {
    formikProps.setFieldValue("type", item);
    this.props.management.setModalThemeType(item);
  };

  componentDidMount() {
    this.props.management.setModalThemeType(this.props.initialValues.type);
  }

  render() {
    return (
      <Formik
        initialValues={this.props.initialValues}
        validationSchema={getValidationSchema(
          this.props.management.modalThemeType === this.props.values.themeTypes.Login
        )}
        onSubmit={this.props.onSubmit}
        render={(formikProps: FormikProps<Theme>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <STFormTitleTypography>{this.props.title}</STFormTitleTypography>
            <STContentWrapper>
              <Grid container justify="space-between" alignItems="center">
                <Grid item>{renderCommonFormField("name", "name", formikProps)}</Grid>
                <Grid item>
                  <STFormSimpleSelect<IFormValues, Theme>
                    fieldName="type"
                    items={this.themeTypes}
                    keyAsTitle="name"
                    keyAsValue="value"
                    formikProps={formikProps}
                    disabled={this.props.isEdit}
                    onChange={this.handleTypeChange(formikProps)}
                    label={Values.getLocalizationValue("themeType")}
                  />
                </Grid>
              </Grid>

              {renderCommonFormField("description", "description", formikProps)}

              {this.props.auth.user.location.primary && (
                <Grid container justify="flex-start" alignItems="center">
                  <Grid item xs={7}>
                    <LocationFormSimpleSelect<Theme, Location>
                      fieldName="locationId"
                      items={this.props.management.activeLocations}
                      keyAsTitle="name"
                      keyAsValue="id"
                      formikProps={formikProps}
                      label={Values.getLocalizationValue("location")}
                      disabled={!this.isNew}
                    />
                  </Grid>
                </Grid>
              )}

              {this.getIsAppTheme(formikProps) ? (
                <>
                  <FileField<Theme>
                    key="applicationThemeDetails.applicationLogoImage"
                    fieldName="applicationThemeDetails.applicationLogoImage"
                    label={Values.getLocalizationValue("logo")}
                    formikProps={formikProps}
                    isEdit={this.props.isEdit}
                    imageUrl={this.props.applicationLogoImageUrl}
                    hasImageSet={Boolean(this.props.applicationLogoImageUrl)}
                  />
                  <ColorField<Theme>
                    fieldName="applicationThemeDetails.primaryColor"
                    label={Values.getLocalizationValue("primaryColor")}
                    formikProps={formikProps}
                  />
                </>
              ) : (
                <>
                  <FileField<Theme>
                    key="loginThemeDetails.loginBackgroundLandscapeImage"
                    fieldName="loginThemeDetails.loginBackgroundLandscapeImage"
                    label={Values.getLocalizationValue("loginBackgroundLandscapeImage")}
                    formikProps={formikProps}
                    isEdit={this.props.isEdit}
                    imageUrl={this.props.loginBackgroundLandscapeImageUrl}
                    hasImageSet={Boolean(this.props.loginBackgroundLandscapeImageUrl)}
                  />
                  <FileField<Theme>
                    key="loginThemeDetails.loginBackgroundPortraitImage"
                    fieldName="loginThemeDetails.loginBackgroundPortraitImage"
                    label={Values.getLocalizationValue("loginBackgroundPortraitImage")}
                    formikProps={formikProps}
                    isEdit={this.props.isEdit}
                    imageUrl={this.props.loginBackgroundPortraitImageUrl}
                    hasImageSet={Boolean(this.props.loginBackgroundPortraitImageUrl)}
                  />
                  <FileField<Theme>
                    key="loginThemeDetails.loginLogoImage"
                    fieldName="loginThemeDetails.loginLogoImage"
                    label={Values.getLocalizationValue("logo")}
                    formikProps={formikProps}
                    isEdit={this.props.isEdit}
                    imageUrl={this.props.loginLogoImageUrl}
                    hasImageSet={Boolean(this.props.loginLogoImageUrl)}
                  />
                </>
              )}

              <ColorField<Theme>
                fieldName="secondaryColor"
                label={Values.getLocalizationValue("secondaryColor")}
                formikProps={formikProps}
              />
              <Field
                name="css"
                render={({ field }: FieldProps) => (
                  <TextField {...field} fullWidth multiline rows="5" label={Values.getLocalizationValue("css")} />
                )}
              />

              <STGrid container justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("nightTheme")}</Grid>
                <Grid item>
                  <FormSwitch<Theme> trueAs={true} falseAs={false} fieldName="isNightTheme" formikProps={formikProps} />
                </Grid>
              </STGrid>

              <STGrid container justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("active")}</Grid>
                <Grid item>
                  <FormSwitch<Theme>
                    trueAs={this.props.values.states.Active}
                    falseAs={this.props.values.states.Deactivated}
                    fieldName="state"
                    formikProps={formikProps}
                  />
                </Grid>
              </STGrid>
            </STContentWrapper>
            <ButtonsForm onSubmit={formikProps.handleSubmit} onClose={this.props.onClose} />
          </STFormWrapper>
        )}
      />
    );
  }
}

export default ThemeForm;
